<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Empresas</h1>
      <button
        @click="showCreateModal = true"
        class="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
      >
        <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        Nova Empresa
      </button>
    </div>
    
    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
      <p class="mt-4 text-gray-600">Carregando empresas...</p>
    </div>
    
    <!-- Empty -->
    <div v-else-if="companies.length === 0" class="bg-white rounded-xl shadow-sm p-8 text-center">
      <p class="text-gray-500">Nenhuma empresa cadastrada</p>
    </div>
    
    <!-- Table -->
    <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logo</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="company in companies" :key="company.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap">
              <img
                v-if="company.logo_url"
                :src="getImageUrl(company.logo_url)"
                class="h-10 w-10 rounded-lg object-cover"
              />
              <div v-else class="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                <span class="text-gray-500 font-bold">{{ company.name.charAt(0) }}</span>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-gray-900">{{ company.name }}</div>
              <div class="text-sm text-gray-500">{{ company.slug }}</div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <a
                v-if="company.custom_domain"
                :href="`http://${company.custom_domain}`"
                target="_blank"
                class="text-sm text-orange-600 hover:text-orange-800 block"
              >
                {{ company.custom_domain }}
              </a>
              <a
                v-else-if="company.subdomain"
                :href="`http://${company.subdomain}.${company.custom_domain_base || company.domain_base || 'deliveryapphub.com'}`"
                target="_blank"
                class="text-sm text-orange-600 hover:text-orange-800 block"
              >
                {{ company.subdomain }}.{{ company.custom_domain_base || company.domain_base || 'deliveryapphub.com' }}
              </a>
              <span v-else class="text-sm text-gray-500">—</span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ company.email }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="company.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
              >
                {{ company.is_active ? 'Ativa' : 'Inativa' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button
                @click="editCompany(company)"
                class="text-orange-600 hover:text-orange-900 mr-3"
              >
                Editar
              </button>
              <button
                @click="toggleActive(company)"
                class="text-gray-600 hover:text-gray-900 mr-3"
              >
                {{ company.is_active ? 'Desativar' : 'Ativar' }}
              </button>
              <button
                @click="deleteCompany(company)"
                class="text-red-600 hover:text-red-900"
              >
                Excluir
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Create/Edit Modal -->
    <CompanyFormModal
      v-if="showCreateModal || showEditModal"
      :company="selectedCompany"
      :is-edit="showEditModal"
      @close="closeModal"
      @save="handleSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { companyApi } from '@/utils/api'
import { notifyError } from '@/utils/notifications'
import { confirmDialog } from '@/composables/useConfirm'
import type { Company } from '@/types'
import CompanyFormModal from './CompanyFormModal.vue'

const companies = ref<Company[]>([])
const loading = ref(false)
const showCreateModal = ref(false)
const showEditModal = ref(false)
const selectedCompany = ref<Company | null>(null)

const getImageUrl = (url: string) => {
  if (url.startsWith('http')) return url
  return url
}

const loadCompanies = async () => {
  loading.value = true
  try {
    const response = await companyApi.getAll()
    companies.value = response.data
  } catch (error) {
    notifyError('Erro ao carregar empresas')
  } finally {
    loading.value = false
  }
}

const editCompany = (company: Company) => {
  selectedCompany.value = company
  showEditModal.value = true
}

const closeModal = () => {
  showCreateModal.value = false
  showEditModal.value = false
  selectedCompany.value = null
}

const handleSave = () => {
  closeModal()
  loadCompanies()
}

const toggleActive = async (company: Company) => {
  try {
    await companyApi.update(company.id, { is_active: !company.is_active })
    loadCompanies()
  } catch (error) {
    notifyError('Erro ao atualizar status')
  }
}

const deleteCompany = async (company: Company) => {
  if (!await confirmDialog({ title: 'Confirmar exclusão', message: `Tem certeza que deseja excluir a empresa "${company.name}"?`, danger: true, confirmLabel: 'Excluir' })) {
    return
  }
  
  try {
    await companyApi.delete(company.id)
    loadCompanies()
  } catch (error) {
    notifyError('Erro ao excluir empresa')
  }
}

onMounted(() => {
  loadCompanies()
})
</script>
